import { apiClient } from '../lib/apiClient';

const ensureArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value == null) return [];
  return [value];
};

const normalizeArrayResponse = (response) => {
  if (!response) return [];
  const { data } = response;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.result)) return data.result;
  return [];
};

const toNumber = (value, fallback = 0) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const cleaned = value.replace(/[^0-9.-]+/g, '');
    const parsed = Number.parseFloat(cleaned);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return fallback;
};

const mapOrderItem = (item) => {
  if (!item) return null;
  const productId =
    item.productId ?? item.id ?? item.sku ?? item.productCode ?? item.variantId ?? item.product_id ?? null;
  const quantity = toNumber(item.quantity ?? item.qty ?? item.orderQty ?? 1, 1);
  const price = toNumber(item.price ?? item.unitPrice ?? item.amount ?? item.totalPrice ?? 0, 0);

  return {
    productId,
    quantity,
    price,
    raw: item,
  };
};

export const mapOrder = (order) => {
  if (!order) return null;

  const orderId =
    order.orderId ??
    order.id ??
    order.orderNumber ??
    order.orderNo ??
    order.orderCode ??
    order.code ??
    `order-${Math.random().toString(36).slice(2)}`;

  const status = (order.status ?? order.orderStatus ?? '').toString().toLowerCase();
  const total = toNumber(order.total ?? order.totalAmount ?? order.grandTotal ?? order.orderAmount ?? 0, 0);
  const createdAt = order.date ?? order.createdAt ?? order.createdDate ?? order.orderDate ?? new Date().toISOString();
  const items = ensureArray(order.items ?? order.orderItems ?? order.orderItemDtoList).map(mapOrderItem).filter(Boolean);

  const shippingAddress =
    order.shippingAddress ??
    order.address ??
    order.deliveryAddress ??
    order.recipientAddress ??
    order.customerAddress ??
    '';
  const trackingNumber = order.trackingNumber ?? order.trackingNo ?? order.shipmentTrackingNumber ?? null;
  const estimatedDelivery =
    order.estimatedDelivery ?? order.deliveryDate ?? order.expectedDelivery ?? order.eta ?? null;

  return {
    id: orderId,
    status,
    total,
    date: createdAt,
    items,
    shippingAddress,
    trackingNumber,
    estimatedDelivery,
    raw: order,
  };
};

export const normalizeOrders = (orders) => ensureArray(orders).map(mapOrder).filter(Boolean);

export const fetchOrders = async () => {
  const response = await apiClient.get('/usermgmt/getAllOrder');
  return normalizeOrders(normalizeArrayResponse(response));
};

/**
 * Create a new order
 * @param {Object} orderData - Order data to be sent to the API
 * @param {number} orderData.tradeCreditId - Trade credit ID
 * @param {string} orderData.salesOrderNumber - Sales order number
 * @param {string} orderData.purchaseOrderNumber - Purchase order number
 * @param {string} orderData.Channel - Order channel
 * @param {string} orderData.packingSlipNumber - Packing slip number
 * @param {string} orderData.firstName - Customer first name
 * @param {string} orderData.lastName - Customer last name
 * @param {string} orderData.phone - Customer phone
 * @param {string} orderData.email - Customer email
 * @param {string} orderData.customerCountry - Customer country
 * @param {string} orderData.customerState - Customer state
 * @param {string} orderData.customerCity - Customer city
 * @param {string} orderData.customerStreet - Customer street
 * @param {string} orderData.customerZipcode - Customer zipcode
 * @param {number} orderData.customerAccountId - Customer account ID
 * @param {string} orderData.orderStatus - Order status
 * @param {string} orderData.shippingMethod - Shipping method
 * @param {string} orderData.shippingCharges - Shipping charges
 * @param {string} orderData.billingCountry - Billing country
 * @param {string} orderData.billingState - Billing state
 * @param {string} orderData.billingCity - Billing city
 * @param {string} orderData.billingStreet - Billing street
 * @param {string} orderData.billingZipcode - Billing zipcode
 * @param {string} orderData.shippingCountry - Shipping country
 * @param {string} orderData.paymentType - Payment type
 * @param {number} orderData.onAccountTransactionNumber - On account transaction number
 * @param {number} orderData.maskedCardNumber - Masked card number
 * @param {string} orderData.shippingState - Shipping state
 * @param {string} orderData.shippingCity - Shipping city
 * @param {string} orderData.shippingStreet - Shipping street
 * @param {string} orderData.shippingZipcode - Shipping zipcode
 * @param {string} orderData.subTotal - Subtotal
 * @param {string} orderData.shippingCost - Shipping cost
 * @param {string} orderData.discount - Discount
 * @param {string} orderData.GST - GST amount
 * @param {string} orderData.totalPrice - Total price
 * @param {string} orderData.notes - Order notes
 * @param {Array<string>} orderData.addProducts - Array of product IDs or SKUs to add
 * @returns {Promise<Object>} Response data from the API
 */
export const createOrder = async (orderData) => {
  try {
    const payload = {
      tradeCreditId: orderData.tradeCreditId ?? 0,
      salesOrderNumber: orderData.salesOrderNumber ?? '',
      purchaseOrderNumber: orderData.purchaseOrderNumber ?? '',
      Channel: orderData.Channel ?? orderData.channel ?? '',
      packingSlipNumber: orderData.packingSlipNumber ?? '',
      firstName: orderData.firstName ?? '',
      lastName: orderData.lastName ?? '',
      phone: orderData.phone ?? '',
      email: orderData.email ?? '',
      customerCountry: orderData.customerCountry ?? '',
      customerState: orderData.customerState ?? '',
      customerCity: orderData.customerCity ?? '',
      customerStreet: orderData.customerStreet ?? '',
      customerZipcode: orderData.customerZipcode ?? '',
      customerAccountId: orderData.customerAccountId ?? 0,
      orderStatus: orderData.orderStatus ?? orderData.status ?? 'pending',
      shippingMethod: orderData.shippingMethod ?? '',
      shippingCharges: orderData.shippingCharges ?? orderData.shippingCost ?? '0',
      billingCountry: orderData.billingCountry ?? orderData.customerCountry ?? '',
      billingState: orderData.billingState ?? orderData.customerState ?? '',
      billingCity: orderData.billingCity ?? orderData.customerCity ?? '',
      billingStreet: orderData.billingStreet ?? orderData.customerStreet ?? '',
      billingZipcode: orderData.billingZipcode ?? orderData.customerZipcode ?? '',
      shippingCountry: orderData.shippingCountry ?? orderData.customerCountry ?? '',
      paymentType: orderData.paymentType ?? '',
      onAccountTransactionNumber: orderData.onAccountTransactionNumber ?? 0,
      maskedCardNumber: orderData.maskedCardNumber ?? 0,
      shippingState: orderData.shippingState ?? orderData.customerState ?? '',
      shippingCity: orderData.shippingCity ?? orderData.customerCity ?? '',
      shippingStreet: orderData.shippingStreet ?? orderData.customerStreet ?? '',
      shippingZipcode: orderData.shippingZipcode ?? orderData.customerZipcode ?? '',
      subTotal: orderData.subTotal ?? orderData.subtotal ?? '0',
      shippingCost: orderData.shippingCost ?? orderData.shippingCharges ?? '0',
      discount: orderData.discount ?? '0',
      GST: orderData.GST ?? orderData.gst ?? orderData.tax ?? '0',
      totalPrice: orderData.totalPrice ?? orderData.total ?? '0',
      notes: orderData.notes ?? orderData.note ?? '',
      addProducts: ensureArray(orderData.addProducts ?? orderData.products ?? orderData.items ?? []),
    };

    const response = await apiClient.post('/usermgmt/createOrder', payload);
    return response.data;
  } catch (error) {
    console.error('Error creating order:', error);
    throw error;
  }
};

export default fetchOrders;

